/**
 * A tab left open across a deploy keeps running the old build until it is reloaded: users saw a
 * different Timefly from the owner's until Cmd+R (owner, 2026-09-24). The page is served no-store
 * and every build's script has a new hashed name (web/nginx.conf), so the live page always names
 * the current build. This compares it with the script this tab runs, when the tab comes back into
 * view and every few minutes, and says so; the shell offers the reload. It never reloads by itself,
 * which could throw away a form being filled in.
 */
import { useEffect, useState } from 'react';

/** The build's hashed script this tab was loaded with; null on the dev server, which has none. */
export const runningScript = (doc: Document = document): string | null =>
  doc.querySelector<HTMLScriptElement>('script[type="module"][src^="/assets/"]')?.getAttribute('src') ?? null;

/** The build's hashed script a page names. */
export const scriptIn = (html: string): string | null =>
  /<script\b[^>]*\btype="module"[^>]*\bsrc="(\/assets\/[^"]+)"/.exec(html)?.[1] ?? null;

/** True once the live page names a different build from the one this tab runs. */
export function useNewVersion(everyMs = 5 * 60_000): boolean {
  const [stale, setStale] = useState(false);
  useEffect(() => {
    // the dev server serves the source, which has no build to compare
    const mine = runningScript();
    if (!mine) return;
    let off = false;
    const check = async () => {
      try {
        const live = scriptIn(await (await fetch('/', { cache: 'no-store' })).text());
        if (!off && live && live !== mine) setStale(true);
      } catch {
        // offline or the server restarting: ask again next time
      }
    };
    const onShow = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onShow);
    window.addEventListener('focus', onShow);
    const timer = setInterval(() => void check(), everyMs);
    return () => {
      off = true;
      document.removeEventListener('visibilitychange', onShow);
      window.removeEventListener('focus', onShow);
      clearInterval(timer);
    };
  }, [everyMs]);
  return stale;
}
